// frontend/src/components/FirstRunProfile.tsx
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import i18n from '@/i18n';
import { api, formatApiError } from '@/lib/api';
import type { ClientType, Locale, ProfileInput } from '@/lib/api';

const COUNTRIES: ProfileInput['country'][] = ['NL', 'BE', 'DE', 'FR', 'PL', 'GB', 'IE', 'other'];
const CLIENT_TYPES: ClientType[] = ['brand', 'distributor', 'etailer'];
const LANGUAGES: Locale[] = ['nl', 'en', 'de', 'fr', 'pl'];

/**
 * The country a language points at, to pre-select on the first screen. It was
 * NL for everybody: a German who left it standing got a Dutch home market, and
 * every .de shop he tried to add afterwards was refused as "another country".
 * English points at no country in particular, so it pre-selects nothing and
 * the person has to choose.
 */
export function countryForLanguage(language: string | undefined): ProfileInput['country'] | '' {
  const byLanguage: Record<string, ProfileInput['country']> = { nl: 'NL', de: 'DE', fr: 'FR', pl: 'PL' };
  return byLanguage[(language ?? '').split('-')[0] ?? ''] ?? '';
}

/**
 * The first screen after the first login. Four questions, once.
 *
 * Three of the four were guesses until now: signing up asks for a company name
 * and an e-mail address, and provisioning filled in the rest -- `client_type`
 * hard-coded to "brand", `home_country` to "NL", and no name for the person at
 * all. A Belgian e-tailer was recorded as a Dutch brand, got a Dutch brand's
 * alert detectors, and was asked to add the webshop he does not have.
 *
 * It keys on the person's own name being empty rather than on a "seen it" flag:
 * that is the same fact, and a flag would be a second place for it to be wrong.
 * It blocks the app because answering takes fifteen seconds and every screen
 * behind it reads better once it is answered.
 */
export function FirstRunProfile({ children }: { children: React.ReactNode }) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const profile = useQuery({
    queryKey: ['me', 'profile'],
    queryFn: () => api.me.profile(),
    // A failure here must never lock somebody out of his own app: no retry, and
    // the guard below lets go on an error.
    retry: false,
  });

  if (profile.isPending || profile.isError || profile.data?.complete) return <>{children}</>;
  return <ProfileForm onSaved={() => queryClient.invalidateQueries()} t={t} />;
}

function ProfileForm({
  onSaved,
  t,
}: {
  onSaved: () => void;
  t: ReturnType<typeof useTranslation>['t'];
}) {
  const [name, setName] = useState('');
  const [country, setCountry] = useState<ProfileInput['country'] | ''>(() => countryForLanguage(i18n.resolvedLanguage));
  const [language, setLanguage] = useState<Locale>((i18n.resolvedLanguage as Locale) || 'nl');
  const [clientType, setClientType] = useState<ClientType>('brand');
  const [error, setError] = useState<string | null>(null);

  const save = useMutation({
    mutationFn: (chosen: ProfileInput['country']) =>
      api.me.saveProfile({ name: name.trim(), country: chosen, language, client_type: clientType }),
    onSuccess: async () => {
      // Switch the interface to the language he just picked, before the app
      // behind this screen renders a single word in the other one.
      await i18n.changeLanguage(language);
      onSaved();
    },
    onError: (err) => setError(formatApiError(err, t('firstRun.saveFailed'))),
  });

  const field = 'w-full bg-bg-primary border border-border-subtle rounded-md px-sm py-sm text-body-sm focus:outline-hidden focus-visible:border-accent';

  return (
    <main className="min-h-screen bg-bg-primary text-text-primary grid place-items-center px-lg">
      <div className="w-full max-w-[28rem]">
        <h1 className="font-sans text-h2 mb-xs">{t('firstRun.title')}</h1>
        <p className="text-body-sm text-text-secondary mb-lg">{t('firstRun.intro')}</p>
        <form
          className="space-y-md bg-bg-secondary border border-border-subtle rounded-md p-lg"
          onSubmit={(e) => {
            e.preventDefault();
            setError(null);
            if (country) save.mutate(country);
          }}
        >
          {error && <p className="text-body-sm text-danger">{error}</p>}
          <label className="block">
            <span className="block text-body-sm text-text-secondary mb-xs">{t('firstRun.name')}</span>
            <input
              type="text" required minLength={2} maxLength={120} autoComplete="name"
              value={name} onChange={(e) => setName(e.target.value)} className={field}
            />
          </label>
          <label className="block">
            <span className="block text-body-sm text-text-secondary mb-xs">{t('firstRun.country')}</span>
            <select
              required value={country} className={field}
              onChange={(e) => setCountry(e.target.value as ProfileInput['country'])}
            >
              {!country && <option value="" disabled>{t('firstRun.countryPlaceholder')}</option>}
              {COUNTRIES.map((c) => (
                <option key={c} value={c}>{t(`firstRun.countries.${c}`)}</option>
              ))}
            </select>
          </label>
          <label className="block">
            <span className="block text-body-sm text-text-secondary mb-xs">{t('firstRun.language')}</span>
            <select value={language} onChange={(e) => setLanguage(e.target.value as Locale)} className={field}>
              {LANGUAGES.map((l) => (
                <option key={l} value={l}>{t(`firstRun.languages.${l}`)}</option>
              ))}
            </select>
          </label>
          <fieldset className="block">
            <legend className="block text-body-sm text-text-secondary mb-xs">{t('firstRun.clientType')}</legend>
            <div className="space-y-xs">
              {CLIENT_TYPES.map((ct) => (
                <label key={ct} className="flex items-start gap-sm text-body-sm">
                  <input
                    type="radio" name="client_type" value={ct} checked={clientType === ct}
                    onChange={() => setClientType(ct)} className="mt-[3px]"
                  />
                  <span>
                    {t(`firstRun.clientTypes.${ct}.label`)}
                    <span className="block text-caption text-text-muted">
                      {t(`firstRun.clientTypes.${ct}.hint`)}
                    </span>
                  </span>
                </label>
              ))}
            </div>
          </fieldset>
          <button
            type="submit" disabled={save.isPending || !country}
            className="w-full bg-accent text-on-accent rounded-md py-sm text-body-sm font-medium disabled:opacity-60"
          >
            {save.isPending ? t('firstRun.saving') : t('firstRun.save')}
          </button>
        </form>
      </div>
    </main>
  );
}
